import React from 'react';
import { ShareIcon } from './icons';
import { resolveRoddoDarkMode } from '../constants/theme';

const clean = (value: unknown) => String(value ?? '').trim();

const CATEGORY_LABELS: Record<string, string> = {
  rods: 'Rods',
  reels: 'Reels',
  tackle: 'Tackle',
  electronics: 'Electronics',
  boats_kayaks: 'Boats / Kayaks',
  apparel: 'Apparel',
  storage: 'Storage',
  other: 'Other',
};

const CONDITION_LABELS: Record<string, string> = {
  new: 'New',
  like_new: 'Like new',
  good: 'Good',
  fair: 'Fair',
  parts: 'Parts / repair',
};

function formatMoney(priceCents: unknown, currency: unknown) {
  const cents = Math.max(0, Math.round(Number(priceCents || 0)));
  const code = clean(currency || 'usd').toUpperCase() || 'USD';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  } catch {
    return `${code} ${(cents / 100).toFixed(2)}`;
  }
}

function pickImages(item: any): string[] {
  const values = Array.isArray(item?.imageUrls)
    ? item.imageUrls
    : Array.isArray(item?.images)
      ? item.images
      : [];
  const cleaned = values.map((value: any) => clean(value)).filter(Boolean);
  if (cleaned.length) return cleaned;
  const legacy = clean(item?.imageUrl || item?.image || item?.photoUrl);
  return legacy ? [legacy] : [];
}

function fulfillmentLabel(item: any) {
  const methods: string[] = Array.isArray(item?.fulfillmentMethods)
    ? item.fulfillmentMethods
    : ['LOCAL_PICKUP'];
  const labels: string[] = [];
  if (methods.includes('LOCAL_PICKUP')) labels.push('Local pickup');
  if (methods.includes('SHIPPING')) labels.push('Shipping available');
  return labels.join(' · ') || 'Contact seller';
}

type Props = {
  docKey: string;
  item: any;
  isMine: boolean;
  isDarkMode?: boolean;
  onBack: () => void;
  onShare: () => void;
  onMessageSeller: () => void;
  onMakeOffer: () => void;
  onOpenMessages: () => void;
  onMarkPending: () => void;
  onMakeAvailable: () => void;
  onMarkSold: () => void;
  unreadMessageCount?: number;
  onEdit: () => void;
  onArchive: () => void;
  onDelete: () => void;
  onReport: () => void;
  onOpenImage?: (url: string) => void;
};

export default function BazaarListingDetailView({
  docKey,
  item,
  isMine,
  isDarkMode: explicitIsDarkMode,
  onBack,
  onShare,
  onMessageSeller,
  onMakeOffer,
  onOpenMessages,
  onMarkPending,
  onMakeAvailable,
  onMarkSold,
  unreadMessageCount = 0,
  onEdit,
  onArchive,
  onDelete,
  onReport,
  onOpenImage,
}: Props) {
  const isDarkMode = resolveRoddoDarkMode(explicitIsDarkMode);
  const images = React.useMemo(() => pickImages(item), [item]);
  const imageSetKey = React.useMemo(() => images.join('\u001f'), [images]);
  const [activeImageIndex, setActiveImageIndex] = React.useState(0);
  const [sellerMenuOpen, setSellerMenuOpen] = React.useState(false);
  const [statusAction, setStatusAction] = React.useState<'pending' | 'available' | 'sold' | null>(null);
  const [statusDots, setStatusDots] = React.useState(1);

  React.useEffect(() => {
    setActiveImageIndex((current) => (current === 0 ? current : 0));
  }, [docKey, imageSetKey]);

  React.useEffect(() => {
    if (!statusAction) {
      setStatusDots(1);
      return;
    }
    const timer = window.setInterval(() => {
      setStatusDots((current) => (current >= 3 ? 1 : current + 1));
    }, 350);
    return () => window.clearInterval(timer);
  }, [statusAction]);

  const runStatusAction = React.useCallback(async (
    action: 'pending' | 'available' | 'sold',
    callback: () => void | Promise<void>,
  ) => {
    if (statusAction) return;
    setStatusAction(action);
    try {
      await Promise.resolve(callback());
    } finally {
      setStatusAction(null);
    }
  }, [statusAction]);

  const busyLabel = React.useCallback((label: string, action: 'pending' | 'available' | 'sold') =>
    statusAction === action ? `${label}${'.'.repeat(statusDots)}` : label,
  [statusAction, statusDots]);

  const activeImage = images[activeImageIndex] || '';
  const sellerName = clean(item?.sellerUsername || item?.sellerName) || 'Angler';
  const sellerAvatar = clean(item?.sellerAvatar || item?.sellerPhotoURL || item?.sellerPhotoUrl);
  const title = clean(item?.title || item?.name) || 'Untitled listing';
  const description = clean(item?.description);
  const locationLabel = clean(item?.locationLabel) || 'Seller-selected area';
  const category = CATEGORY_LABELS[clean(item?.category)] || 'Other';
  const condition = CONDITION_LABELS[clean(item?.condition)] || 'Used';
  const listingStatus = clean(item?.status || 'active').toLowerCase();
  const isLive = ['active', 'available'].includes(listingStatus);
  const isPending = listingStatus === 'pending';

  React.useEffect(() => {
    setStatusAction(null);
  }, [docKey, listingStatus]);
  const statusLabel = listingStatus === 'sold' ? 'Sold' : listingStatus === 'archived' ? 'Archived' : isPending ? 'Pending' : 'Available';
  const imageStatusLabel = isPending
    ? 'Pending'
    : listingStatus === 'sold'
      ? 'Sold'
      : listingStatus === 'archived'
        ? 'Archived'
        : isMine
          ? 'Yours'
          : '';

  const styles = React.useMemo(() => ({
    page: isDarkMode ? '#070B14' : '#FBF4E9',
    panel: isDarkMode ? '#101827' : '#FFFDF8',
    panelStrong: isDarkMode ? '#0C1320' : '#FFFFFF',
    muted: isDarkMode ? '#0D1422' : '#F4EDE3',
    border: isDarkMode ? 'rgba(226,232,240,0.16)' : 'rgba(74,58,42,0.14)',
    borderStrong: isDarkMode ? 'rgba(226,232,240,0.26)' : 'rgba(74,58,42,0.22)',
    text: isDarkMode ? '#F4F6FA' : '#30271E',
    soft: isDarkMode ? '#A8B4C7' : '#6E6255',
    faint: isDarkMode ? '#728096' : '#938575',
    green: isDarkMode ? '#A7F3D0' : '#17684C',
    accent: '#7657E8',
    gold: '#F2B84B',
  }), [isDarkMode]);

  const actionClass =
    'rounded-[12px] px-4 py-3 text-sm font-black transition-all duration-150 hover:-translate-y-0.5 active:scale-[0.97] active:translate-y-[1px] disabled:cursor-wait disabled:opacity-60 disabled:hover:translate-y-0 disabled:active:scale-100';

  return (
    <div className="px-3 pb-8 pt-3 sm:px-4" style={{ background: styles.page, color: styles.text }}>
      <div className="mx-auto w-full max-w-6xl">
        <div className="mb-3 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={onBack}
            className="grid h-10 w-10 place-items-center rounded-full border text-base font-black transition hover:-translate-y-0.5 active:translate-y-0"
            style={{ background: styles.panel, borderColor: styles.border, color: styles.text }}
            aria-label="Close listing"
            title="Back to Bazaar"
          >
            ✕
          </button>
          <button
            type="button"
            onClick={onShare}
            className="grid h-10 w-10 place-items-center rounded-full border transition hover:-translate-y-0.5 active:translate-y-0"
            style={{ background: styles.panel, borderColor: styles.border, color: styles.text }}
            aria-label="Share listing"
            title="Share listing"
          >
            <ShareIcon className="h-[18px] w-[18px]" />
          </button>
        </div>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.45fr)_minmax(320px,.8fr)] lg:items-start">
          <section
            className="overflow-hidden rounded-[22px] border shadow-[0_16px_38px_rgba(28,20,10,.08)]"
            style={{ background: styles.panel, borderColor: styles.border }}
          >
            <div className="relative aspect-[16/10] w-full overflow-hidden lg:aspect-[4/3]" style={{ background: styles.muted }}>
              {imageStatusLabel ? (
                <span
                  className={`absolute left-3 top-3 z-20 rounded-full px-2.5 py-1 text-[9px] font-black uppercase tracking-[.08em] text-white shadow ${
                    listingStatus === 'sold'
                      ? 'bg-emerald-700'
                      : isPending
                        ? 'bg-amber-600'
                        : listingStatus === 'archived'
                          ? 'bg-slate-600'
                          : 'bg-[#7657E8]'
                  }`}
                >
                  {imageStatusLabel}
                </span>
              ) : null}
              {activeImage ? (
                <button
                  type="button"
                  onClick={() => onOpenImage?.(activeImage)}
                  className="block h-full w-full"
                  aria-label="Open full size listing photo"
                >
                  <img src={activeImage} alt={title} className="h-full w-full object-contain" />
                </button>
              ) : (
                <div className="grid h-full place-items-center text-center">
                  <div>
                    <div className="text-3xl">🎣</div>
                    <div className="mt-2 text-xs font-black uppercase tracking-[.14em]" style={{ color: styles.faint }}>No photo</div>
                  </div>
                </div>
              )}

              {images.length > 1 ? (
                <>
                  <button
                    type="button"
                    onClick={() => setActiveImageIndex((index) => (index - 1 + images.length) % images.length)}
                    className="absolute left-3 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-black/65 text-xl font-black text-white backdrop-blur"
                    aria-label="Previous photo"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveImageIndex((index) => (index + 1) % images.length)}
                    className="absolute right-3 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-black/65 text-xl font-black text-white backdrop-blur"
                    aria-label="Next photo"
                  >
                    ›
                  </button>
                  <div className="absolute bottom-3 right-3 rounded-full bg-black/70 px-2.5 py-1 text-[10px] font-black text-white">
                    {activeImageIndex + 1}/{images.length}
                  </div>
                </>
              ) : null}
            </div>

            {images.length > 1 ? (
              <div className="flex gap-2 overflow-x-auto border-t p-3" style={{ borderColor: styles.border }}>
                {images.map((src, index) => (
                  <button
                    type="button"
                    key={`${src}_${index}`}
                    onClick={() => setActiveImageIndex(index)}
                    className="h-16 w-16 shrink-0 overflow-hidden rounded-[11px] border-2 transition"
                    style={{ borderColor: index === activeImageIndex ? styles.gold : styles.border }}
                    aria-label={`View photo ${index + 1}`}
                  >
                    <img src={src} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            ) : null}
          </section>

          <aside
            className="rounded-[22px] border p-4 shadow-[0_16px_38px_rgba(28,20,10,.08)] sm:p-5 lg:sticky lg:top-3"
            style={{ background: styles.panel, borderColor: styles.border }}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h1 className="text-[22px] font-black leading-tight tracking-[-.02em]">{title}</h1>
                <div className="mt-1 text-[24px] font-black" style={{ color: styles.green }}>
                  {formatMoney(item?.priceCents, item?.currency)}
                </div>
              </div>
              <div className="flex items-start gap-2">
                <div className="flex flex-wrap justify-end gap-1.5">
                  {(isMine || isPending) ? <span className="rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[.08em]" style={{ background: listingStatus === 'sold' ? 'rgba(16,185,129,.12)' : listingStatus === 'pending' ? 'rgba(245,158,11,.14)' : listingStatus === 'archived' ? 'rgba(100,116,139,.12)' : styles.muted, borderColor: styles.border, color: listingStatus === 'sold' ? styles.green : listingStatus === 'pending' ? '#B7791F' : styles.soft }}>{statusLabel}</span> : null}
                  <span className="rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[.08em]" style={{ background: styles.muted, borderColor: styles.border, color: styles.soft }}>{category}</span>
                  <span className="rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[.08em]" style={{ background: styles.muted, borderColor: styles.border, color: styles.soft }}>{condition}</span>
                </div>
                {isMine ? (
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setSellerMenuOpen((open) => !open)}
                      className="grid h-10 w-10 place-items-center rounded-[12px] border text-lg font-black"
                      style={{ background: styles.muted, borderColor: styles.borderStrong, color: styles.text }}
                      aria-label="Listing actions"
                      aria-expanded={sellerMenuOpen}
                    >
                      ⋯
                    </button>
                    {sellerMenuOpen ? (
                      <div
                        className="absolute right-0 top-12 z-30 min-w-[170px] overflow-hidden rounded-[14px] border p-1.5 shadow-[0_18px_42px_rgba(0,0,0,.18)]"
                        style={{ background: styles.panelStrong, borderColor: styles.borderStrong }}
                      >
                        {isLive ? (
                          <>
                            <button type="button" onClick={() => { setSellerMenuOpen(false); onEdit(); }} className="w-full rounded-[10px] px-3 py-2 text-left text-xs font-black hover:bg-black/[.04] dark:hover:bg-white/[.06]">Edit listing</button>
                            <button type="button" onClick={() => { setSellerMenuOpen(false); onArchive(); }} className="w-full rounded-[10px] px-3 py-2 text-left text-xs font-black hover:bg-black/[.04] dark:hover:bg-white/[.06]">Archive</button>
                          </>
                        ) : null}
                        <button type="button" onClick={() => { setSellerMenuOpen(false); onDelete(); }} className="w-full rounded-[10px] px-3 py-2 text-left text-xs font-black text-rose-600 hover:bg-rose-500/10 dark:text-rose-300">Delete</button>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="mt-3 text-xs font-semibold leading-5" style={{ color: styles.soft }}>
              {locationLabel} · Approximate location
            </div>
            <div className="mt-1 text-xs font-semibold" style={{ color: styles.soft }}>{fulfillmentLabel(item)}</div>

            {!isMine && isPending ? (
              <div className="mt-4 rounded-[13px] border border-amber-500/25 bg-amber-400/10 px-3.5 py-3 text-xs font-bold text-amber-800 dark:text-amber-200">
                Pending sale · You can still message the seller, but new offers are paused.
              </div>
            ) : null}

            <div className="mt-4 grid gap-2">
              {isMine ? (
                <div className="grid gap-2">
                  <button type="button" onClick={onOpenMessages} className={`${actionClass} relative bg-[#4EA5E9] text-white`}>
                    Messages
                    {unreadMessageCount > 0 ? <span className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-black text-white">{unreadMessageCount}</span> : null}
                  </button>
                  {isLive ? <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => void runStatusAction('pending', onMarkPending)} disabled={statusAction !== null} aria-busy={statusAction === 'pending'} className={`${actionClass} border border-amber-500/30 bg-amber-400/15 text-xs text-amber-800 dark:text-amber-200`}>{busyLabel('Mark pending', 'pending')}</button>
                    <button type="button" onClick={() => void runStatusAction('sold', onMarkSold)} disabled={statusAction !== null} aria-busy={statusAction === 'sold'} className={`${actionClass} border border-emerald-500/30 bg-emerald-400/15 text-xs text-emerald-800 dark:text-emerald-200`}>{busyLabel('Mark sold', 'sold')}</button>
                  </div> : null}
                  {isPending ? <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => void runStatusAction('available', onMakeAvailable)} disabled={statusAction !== null} aria-busy={statusAction === 'available'} className={`${actionClass} border text-xs`} style={{ borderColor: styles.borderStrong, background: styles.muted, color: styles.text }}>{busyLabel('Make available', 'available')}</button>
                    <button type="button" onClick={() => void runStatusAction('sold', onMarkSold)} disabled={statusAction !== null} aria-busy={statusAction === 'sold'} className={`${actionClass} border border-emerald-500/30 bg-emerald-400/15 text-xs text-emerald-800 dark:text-emerald-200`}>{busyLabel('Mark sold', 'sold')}</button>
                  </div> : null}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={onMessageSeller} className={`${actionClass} relative bg-[#4EA5E9] text-white`}>
                    Message seller
                    {unreadMessageCount > 0 ? (
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-black text-white">{unreadMessageCount}</span>
                    ) : null}
                  </button>
                  <button type="button" onClick={onMakeOffer} disabled={!isLive} className={`${actionClass} ${isPending ? 'border border-amber-500/30 bg-amber-400/15 text-amber-800 dark:text-amber-200' : 'bg-[#F2B84B] text-[#2D2615]'} disabled:cursor-not-allowed`}>
                    {isPending ? 'Pending' : 'Make offer'}
                  </button>
                </div>
              )}
            </div>

            <div className="my-5 border-t" style={{ borderColor: styles.border }} />

            <div>
              <div className="text-[11px] font-black uppercase tracking-[.14em]" style={{ color: styles.faint }}>Details</div>
              <dl className="mt-3 grid grid-cols-[110px_1fr] gap-x-3 gap-y-2 text-sm">
                {(isMine || isPending) ? <><dt className="font-bold" style={{ color: styles.soft }}>Status</dt><dd className="font-black">{statusLabel}</dd></> : null}
                <dt className="font-bold" style={{ color: styles.soft }}>Condition</dt><dd className="font-black">{condition}</dd>
                <dt className="font-bold" style={{ color: styles.soft }}>Category</dt><dd className="font-black">{category}</dd>
                <dt className="font-bold" style={{ color: styles.soft }}>Fulfillment</dt><dd className="font-black">{fulfillmentLabel(item)}</dd>
                <dt className="font-bold" style={{ color: styles.soft }}>Payment</dt><dd className="font-black">Arrange directly with seller</dd>
              </dl>
            </div>

            <div className="my-5 border-t" style={{ borderColor: styles.border }} />

            <div>
              <div className="text-[11px] font-black uppercase tracking-[.14em]" style={{ color: styles.faint }}>Description</div>
              <p className="mt-2 whitespace-pre-wrap text-sm font-semibold leading-6" style={{ color: description ? styles.text : styles.soft }}>
                {description || 'No additional description provided.'}
              </p>
            </div>

            <div className="my-5 border-t" style={{ borderColor: styles.border }} />

            <div>
              <div className="text-[11px] font-black uppercase tracking-[.14em]" style={{ color: styles.faint }}>Seller</div>
              <div className="mt-3 flex items-center gap-3 rounded-[15px] border p-3" style={{ background: styles.panelStrong, borderColor: styles.border }}>
                <div className="h-12 w-12 shrink-0 overflow-hidden rounded-full border" style={{ background: styles.muted, borderColor: styles.border }}>
                  {sellerAvatar ? <img src={sellerAvatar} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center text-sm font-black">{sellerName.slice(0, 1).toUpperCase()}</div>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-black">{sellerName}</div>
                  <div className="mt-1 text-[11px] font-semibold" style={{ color: styles.soft }}>Roddo angler · Bazaar seller</div>
                </div>
              </div>
            </div>

            {!isMine ? (
              <>
                <div className="my-5 border-t" style={{ borderColor: styles.border }} />
                <button type="button" onClick={onReport} className="text-xs font-black text-rose-600 dark:text-rose-300">Report listing</button>
              </>
            ) : null}

            <div className="mt-5 rounded-[14px] border px-3.5 py-3 text-[11px] font-semibold leading-5" style={{ background: styles.muted, borderColor: styles.border, color: styles.soft }}>
              Privacy-first: meet in a safe public place for local pickup. Never share a private fishing spot, unrelated live location, password, or payment credentials in chat.
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
